import jsPDF from 'jspdf';
import moment from 'moment';
import _utils from './Utils';

// Final Result, Selected/Waiting Publication & Appointment Letter Closure phase.
//
// Matches the real sample document's (Jr_Clerk_Final_Selected_Waiting_List.pdf) exact visual
// structure: entity name + recruitment year on one line, post title + "Selected and Waiting
// Candidates List" on the next, then two bold-underlined section headers ("Selected Candidates
// Roll Number" / "Waiting Candidates Roll Number"), each followed by a flowing grid of bare Roll
// Numbers (ascending, evenly spaced columns — no per-candidate table, no names, no marks). Business
// content/ordering is unchanged from the sample; only typography/spacing is modernized.
//
// Roll Numbers come from GetPublishedSelectedWaitingRollNumbers — the live, published FinalStatus
// per candidate — never the staged/Pending preview data, so a PDF can never be generated ahead of
// an actual Final Result publication.
const ROLL_NUMBERS_PER_ROW = 13;

function buildSelectedWaitingListPdf({ entityName, recruitmentLabel, postName, selectedRollNumbers, waitingRollNumbers, generatedAt }) {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 50;
    const bottomLimit = pageHeight - 55;
    let y = 55;

    // PDF Filename Closure phase's own proven technique: the browser's PDF viewer suggests this
    // document's /Title metadata as the save filename — computed from the same
    // buildSelectedWaitingListFileName the caller uses for the actual Publish upload, so the two
    // never drift apart.
    const suggestedFileName = _utils.buildSelectedWaitingListFileName(postName, generatedAt);
    doc.setProperties({ title: suggestedFileName.replace(/\.pdf$/i, '') });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(0, 0, 0);
    const line1 = recruitmentLabel ? `${entityName || ''} - ${recruitmentLabel}` : (entityName || '');
    doc.text(line1, pageWidth / 2, y, { align: 'center' });
    y += 22;

    const line2 = `${(postName || '').toUpperCase()} - Selected and Waiting Candidates List`;
    doc.text(line2, pageWidth / 2, y, { align: 'center' });
    y += 50;

    const ensureSpace = (needed) => {
        if (y + needed > bottomLimit) {
            doc.addPage();
            y = 55;
        }
    };

    const drawSectionHeader = (label) => {
        ensureSpace(30);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(label, marginX, y);
        const textWidth = doc.getTextWidth(label);
        doc.setLineWidth(0.75);
        doc.setDrawColor(0, 0, 0);
        doc.line(marginX, y + 3, marginX + textWidth, y + 3);
        y += 28;
    };

    const drawRollNumberGrid = (rollNumbers) => {
        if (!rollNumbers || rollNumbers.length === 0) {
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(10);
            doc.setTextColor(90, 90, 90);
            doc.text('None', marginX, y);
            y += 24;
            return;
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        const usableWidth = pageWidth - marginX * 2;
        const colWidth = usableWidth / ROLL_NUMBERS_PER_ROW;
        const rowHeight = 20;

        for (let i = 0; i < rollNumbers.length; i++) {
            const col = i % ROLL_NUMBERS_PER_ROW;
            if (col === 0 && i !== 0) {
                y += rowHeight;
                ensureSpace(rowHeight);
            }
            const x = marginX + col * colWidth;
            doc.text(String(rollNumbers[i]), x, y);
        }
        y += rowHeight + 34;
    };

    drawSectionHeader('Selected Candidates Roll Number');
    drawRollNumberGrid(selectedRollNumbers);

    drawSectionHeader('Waiting Candidates Roll Number');
    drawRollNumberGrid(waitingRollNumbers);

    const generatedAtText = moment(generatedAt || new Date()).format('DD-MMM-YYYY hh:mm A');
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text('Generated: ' + generatedAtText, marginX, pageHeight - 20);
        doc.text('Page ' + i + ' of ' + totalPages, pageWidth - marginX, pageHeight - 20, { align: 'right' });
    }

    return doc;
}

function buildSelectedWaitingListPdfBlob(params) {
    return buildSelectedWaitingListPdf(params).output('blob');
}

export { buildSelectedWaitingListPdf, buildSelectedWaitingListPdfBlob };
export default buildSelectedWaitingListPdfBlob;
